import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import {
  useAuthState,
  useCreateUserWithEmailAndPassword,
  useSignOut,
} from "react-firebase-hooks/auth";
import { updateProfile } from "firebase/auth";
import { auth } from "../config/firebaseConfig";
import { useProfile } from "../context/ProfileContext";
import {
  mapFirebaseCreateUserError,
  validatePasswordPair,
} from "@/lib/authSignUpValidation";
import {
  trackOnboardingStarted,
  trackUserRegistrationCompleted,
  trackUserRegistrationError,
  trackBusinessRegistrationStarted,
  trackBusinessRegistrationCompleted,
  trackBusinessRegistrationError,
  trackOnboardingCompleted,
  trackOnboardingStepViewed,
  trackOnboardingStepCompleted,
  trackOnboardingStepSkipped,
  setUserId,
} from "@/lib/analytics";
import { trpcClient } from "@/trpc/client";
import { isEmpty } from "@/lib/helpers";
import { useOnboardingStep } from "@/hooks/use-onboarding-step";
import { getBusinessVertical } from "@graftus/schema";
import { BusinessVerticalChoice } from "@/components/Onboarding/BusinessVerticalChoice";
import { EXTENDED_STEPS, type OnboardingStepKey } from "@/types/onboarding";
import OnboardingShell from "@/components/Onboarding/OnboardingShell";
import OnboardingNavigation from "@/components/Onboarding/OnboardingNavigation";
import { OnboardingDevStepRail } from "@/components/Onboarding/OnboardingDevStepRail";
import { Button } from "@/components/ui/button";

import SetNameStep from "@/components/Onboarding/steps/SetNameStep";
import CreateBusinessStep from "@/components/Onboarding/steps/CreateBusinessStep";
import BusinessProfileStep from "@/components/Onboarding/steps/BusinessProfileStep";
import IntegrationsStep from "@/components/Onboarding/steps/IntegrationsStep";
import SelectProductStep, {
  type OnboardingProductKey,
} from "@/components/Onboarding/steps/SelectProductStep";
import type { StepHandle } from "@/components/Onboarding/steps/CompanyProfileStep";
import type { BusinessData, CompanyIdentity } from "@/types/businessData";
import { track, LogEvents } from "@/analytics/track";
import { persistProfileSection } from "@/lib/persistProfileSection";

const STEP_TITLE_KEYS: Record<OnboardingStepKey, string> = {
  "set-name": "setName",
  "create-business": "createBusiness",
  "business-profile": "businessProfile",
  integrations: "integrations",
  "select-products": "selectProducts",
};

function isCoreOnboardingComplete(bp: BusinessData | null): boolean {
  if (!bp) return false;
  const products = bp.interestedProducts;
  return !!products?.length;
}

export default function OnboardingView() {
  const { t } = useTranslation("onboarding");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userData, setUserData] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [profileData, setProfileData] = useState<Record<string, unknown>>({});

  const [localBusinessId, setLocalBusinessId] = useState<string | null>(null);
  const [localHasBusiness, setLocalHasBusiness] = useState(false);
  const [localWebsite, setLocalWebsite] = useState<string | undefined>(
    undefined,
  );
  /** Set after products are submitted — shows the sales-led confirmation. */
  const [finished, setFinished] = useState(false);

  const [user, loadingUser] = useAuthState(auth);
  const [createUserWithEmailAndPassword] =
    useCreateUserWithEmailAndPassword(auth);
  const [signOut] = useSignOut(auth);

  const handleSignOut = async () => {
    await signOut();
    navigate("/sign-in");
  };

  const menuUser = user
    ? {
        name: user.displayName?.trim() || userData?.name?.trim() || "",
        email: user.email || userData?.email || "",
      }
    : undefined;
  const {
    setProfileCreated,
    businessProfile,
    loading: profileLoading,
  } = useProfile();

  const stepRef = useRef<StepHandle>(null);

  const hasUser = !!user;
  const hasBusiness = !!businessProfile || localHasBusiness;
  const businessId = businessProfile?.uid ?? localBusinessId;
  const website = businessProfile?.website ?? localWebsite;

  const stepMachineReady = !profileLoading;
  // GRA-87: chosen on the profile step; a grocery store skips integrations.
  const [vertical, setVertical] = useState<"sales" | "dvh">(
    getBusinessVertical(businessProfile),
  );
  useEffect(() => {
    setVertical(getBusinessVertical(businessProfile));
  }, [businessProfile]);

  const {
    stepKey,
    setStepKey,
    nextStep,
    prevStep,
    canSkip,
    isFirst,
    isLast,
    totalSteps,
    currentStepNumber,
    visibleSteps,
  } = useOnboardingStep({
    hasUser,
    hasBusiness,
    ready: stepMachineReady,
    devUnlockNavigation: import.meta.env.DEV,
    vertical,
  });

  const coreComplete = isCoreOnboardingComplete(businessProfile);

  // Handle redirect when onboarding is already done. Suppressed while the
  // in-session confirmation is showing — the user leaves via its CTA.
  useEffect(() => {
    if (!user || profileLoading || finished) return;

    if (coreComplete) {
      const urlStep = searchParams.get("s") as OnboardingStepKey | null;
      if (urlStep && (EXTENDED_STEPS as readonly string[]).includes(urlStep)) {
        return;
      }
      navigate("/");
    }
  }, [user, profileLoading, finished, coreComplete, navigate, searchParams]);

  // Sync user data when auth resolves
  useEffect(() => {
    if (user) {
      setUserData({
        name: user.displayName?.trim() || "",
        email: user.email || "",
      });
    }
  }, [user]);

  // Auto-advance past set-name if user exists but no business yet
  useEffect(() => {
    if (user && stepKey === "set-name" && !businessProfile && !profileLoading) {
      trackBusinessRegistrationStarted(user.uid);
      track({
        event: LogEvents.BusinessRegistrationStarted.name,
        channel: LogEvents.BusinessRegistrationStarted.channel,
      });
      setStepKey("create-business");
    }
  }, [user, stepKey, businessProfile, profileLoading, setStepKey]);

  // Track step views
  useEffect(() => {
    trackOnboardingStepViewed(stepKey, user?.uid, businessId ?? undefined);
    track({
      event: LogEvents.OnboardingStepViewed.name,
      channel: LogEvents.OnboardingStepViewed.channel,
      step_key: stepKey,
    });
  }, [stepKey, user?.uid, businessId]);

  // Load existing company profile when business is available
  useEffect(() => {
    if (!businessId) return;
    const loadProfile = async () => {
      try {
        const result = await trpcClient.onboarding.getCompanyProfile.query({
          businessId,
        });
        if (result.companyProfile) {
          setProfileData(result.companyProfile as Record<string, unknown>);
        }
      } catch {
        // Profile not yet created
      }
    };
    loadProfile();
  }, [businessId]);

  // Track onboarding started
  useEffect(() => {
    trackOnboardingStarted();
    track({
      event: LogEvents.OnboardingStarted.name,
      channel: LogEvents.OnboardingStarted.channel,
    });
  }, []);

  // --- Core step handlers ---

  const handleSetName = async (formData: {
    name: string;
    email: string;
    password: string;
    passwordRepeat: string;
  }) => {
    const { name, email, password, passwordRepeat } = formData;

    if (
      isEmpty(name) ||
      isEmpty(email) ||
      isEmpty(password) ||
      isEmpty(passwordRepeat)
    ) {
      setErrorMessage(t("errors.fillAllFields"));
      return;
    }

    const passwordError = validatePasswordPair(password, passwordRepeat);
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(
        email,
        password,
      );

      if (userCredential?.user) {
        try {
          await updateProfile(userCredential.user, { displayName: name });
        } catch {
          // Non-critical
        }
        setUserId(userCredential.user.uid);
        trackUserRegistrationCompleted(userCredential.user.uid, email);
        track({
          event: LogEvents.UserRegistrationCompleted.name,
          channel: LogEvents.UserRegistrationCompleted.channel,
        });
      }

      setUserData({ name, email });
      setErrorMessage(null);

      if (userCredential?.user) {
        trackBusinessRegistrationStarted(userCredential.user.uid);
        track({
          event: LogEvents.BusinessRegistrationStarted.name,
          channel: LogEvents.BusinessRegistrationStarted.channel,
        });
      }
      setStepKey("create-business");
    } catch (error: unknown) {
      const message = mapFirebaseCreateUserError(error);
      setErrorMessage(message);
      trackUserRegistrationError(message);
      track({
        event: LogEvents.UserRegistrationError.name,
        channel: LogEvents.UserRegistrationError.channel,
        error: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (formData: {
    company: string;
    companyNumber: string;
    website: string;
  }) => {
    if (loadingUser) {
      setErrorMessage(t("errors.verifyingLogin"));
      return;
    }
    if (!user) {
      setErrorMessage(t("errors.notAuthenticated"));
      return;
    }

    let name = userData?.name?.trim() || user.displayName?.trim() || "";
    let email = (user.email || userData?.email || "").trim();

    try {
      setLoading(true);
      setErrorMessage(null);

      if (!name || !email) {
        try {
          const state = await trpcClient.businessUser.me.query();
          name = name || state.profile?.name?.trim() || "";
          email = email || state.profile?.email?.trim() || "";
          if (name || email) {
            setUserData((prev) => ({
              name: name || prev?.name || "",
              email: email || prev?.email || "",
            }));
          }
        } catch {
          // Non-critical
        }
      }

      if (!name || !email) {
        setErrorMessage(t("errors.userDataError"));
        return;
      }

      const result = await trpcClient.business.create.mutate({
        company: formData.company,
        orgNumber: formData.companyNumber,
        name,
        email,
        website: formData.website || undefined,
      });

      setLocalBusinessId(result.businessId);
      setLocalWebsite(formData.website || undefined);
      setErrorMessage(null);
      setLocalHasBusiness(true);

      trackBusinessRegistrationCompleted(
        user.uid,
        result.businessId,
        formData.company,
      );
      track({
        event: LogEvents.BusinessRegistrationCompleted.name,
        channel: LogEvents.BusinessRegistrationCompleted.channel,
        business_id: result.businessId,
      });
      setStepKey("business-profile");
    } catch (error: unknown) {
      const errorMsg = t("errors.createBusinessError");
      setErrorMessage(errorMsg);
      trackBusinessRegistrationError(
        user.uid,
        (error as Error)?.message || errorMsg,
      );
      track({
        event: LogEvents.BusinessRegistrationError.name,
        channel: LogEvents.BusinessRegistrationError.channel,
        error: (error as Error)?.message || errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  // Final step: registers product interest. Activation is sales-led — the
  // Graftus team grants entitlements from admin-dashboard, so we finish with
  // a confirmation instead of unlocking anything client-side.
  const handleSelectProducts = async (products: OnboardingProductKey[]) => {
    if (!user || !businessId) {
      setErrorMessage(t("errors.businessDataError"));
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(null);

      await trpcClient.business.setInterestedProducts.mutate({
        businessId,
        interestedProducts: products,
      });
      await trpcClient.onboarding.markComplete.mutate({ businessId });

      setProfileCreated?.(true);

      trackOnboardingStepCompleted("select-products", user.uid, businessId);
      track({
        event: LogEvents.ProductSelected.name,
        channel: LogEvents.ProductSelected.channel,
        interested_products: products,
      });
      trackOnboardingCompleted(user.uid, businessId);
      track({
        event: LogEvents.OnboardingCompleted.name,
        channel: LogEvents.OnboardingCompleted.channel,
      });

      setFinished(true);
    } catch {
      setErrorMessage(t("errors.saveError"));
    } finally {
      setLoading(false);
    }
  };

  // --- Extended step save handler ---

  const saveExtendedStep = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      setErrorMessage(null);

      if (stepKey === "business-profile") {
        const data = stepRef.current?.getData();
        if (!data) return;
        await persistProfileSection(businessId, "company-profile", data);
      }
      // The integrations step persists itself through the OAuth flow —
      // "Next" just advances.

      trackOnboardingStepCompleted(stepKey, user?.uid, businessId ?? undefined);
      track({
        event: LogEvents.OnboardingStepCompleted.name,
        channel: LogEvents.OnboardingStepCompleted.channel,
        step_key: stepKey,
      });

      nextStep();
    } catch {
      setErrorMessage(t("errors.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setErrorMessage(null);
    if ((EXTENDED_STEPS as readonly string[]).includes(stepKey)) {
      saveExtendedStep();
    }
  };

  const handleSkip = () => {
    trackOnboardingStepSkipped(stepKey, user?.uid, businessId ?? undefined);
    track({
      event: LogEvents.OnboardingStepSkipped.name,
      channel: LogEvents.OnboardingStepSkipped.channel,
      step_key: stepKey,
    });
    nextStep();
  };

  // --- Navigation visibility ---

  const showExtendedNav =
    !finished && (EXTENDED_STEPS as readonly string[]).includes(stepKey);

  const cardTitle = finished
    ? t("done.title")
    : t(`stepTitles.${STEP_TITLE_KEYS[stepKey]}`, {
        defaultValue: "Onboarding",
      });

  const stepperSteps = visibleSteps.map((key) => ({
    key,
    label: t(`stepTitles.${STEP_TITLE_KEYS[key]}`, {
      defaultValue: key,
    }),
  }));
  const overline = t("navigation.progress", {
    current: finished ? totalSteps : currentStepNumber,
    total: totalSteps,
  });

  if (profileLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <OnboardingShell
        steps={stepperSteps}
        currentIndex={currentStepNumber - 1}
        overline={overline}
        title={cardTitle}
        finished={finished}
        user={menuUser}
        onSignOut={() => void handleSignOut()}
      >
        {finished ? (
          <div className="space-y-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/10">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-muted-foreground">
              {t("done.body")}
            </p>
            <Button
              size="lg"
              className="bg-foreground text-background hover:bg-foreground/90"
              onClick={() => navigate("/")}
            >
              {t("done.cta")}
            </Button>
          </div>
        ) : (
          <>
            {stepKey === "set-name" && (
              <SetNameStep
                onSubmit={handleSetName}
                errorMsg={errorMessage}
                loading={loading}
              />
            )}

            {stepKey === "create-business" && (
              <CreateBusinessStep
                onSubmit={handleCreateBusiness}
                errorMsg={errorMessage}
                loading={loading || loadingUser}
                userEmail={user?.email || userData?.email || ""}
              />
            )}

            {stepKey === "business-profile" && (
              <BusinessVerticalChoice
                businessId={businessId}
                value={vertical}
                onChange={setVertical}
              />
            )}

            {stepKey === "business-profile" && (
              <BusinessProfileStep
                ref={stepRef}
                businessId={businessId}
                website={website}
                initialData={
                  profileData.companyIdentity as CompanyIdentity | undefined
                }
                errorMsg={errorMessage}
              />
            )}

            {stepKey === "integrations" && (
              <IntegrationsStep
                businessId={businessId}
                errorMsg={errorMessage}
              />
            )}

            {stepKey === "select-products" && (
              <div className="space-y-4">
                <SelectProductStep
                  onSubmit={handleSelectProducts}
                  errorMsg={errorMessage}
                  loading={loading}
                />
                <div className="flex justify-start border-t border-border pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={prevStep}
                    disabled={loading}
                    className="gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("navigation.back")}
                  </Button>
                </div>
              </div>
            )}

            {showExtendedNav && (
              <OnboardingNavigation
                onBack={prevStep}
                onNext={handleNext}
                onSkip={handleSkip}
                canSkip={canSkip}
                isFirst={isFirst}
                isLast={isLast}
                loading={loading}
              />
            )}
          </>
        )}
      </OnboardingShell>
      <OnboardingDevStepRail
        steps={visibleSteps}
        activeStep={stepKey}
        onJump={setStepKey}
      />
    </>
  );
}
